import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, OnInit, ViewChild, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { EMPTY, Subject, catchError, exhaustMap, from } from 'rxjs';
import { samlPost } from '../../../../functions/saml-post';
import { ClaimRow } from '../../../../models/api/view/claim-view/claim-row';
import { DocImage } from '../../../../models/api/view/claim/documentation/documents/doc-image';
import { SirSSO } from '../../../../models/api/view/claim/documentation/documents/sir-sso';
import { DocumentMock } from '../../../../models/core/mock/document-mock';
import { Snackbar } from '../../../../services/snackbar/snackbar.service';
import { BaseComponent } from '../../../base/base.component';
import { ButtonComponent } from '../../../core/buttons/button.component';
import { LinkLabelComponent } from '../../../core/labels/link-label.component';
import { PageHeaderComponent } from '../../../core/labels/page-header.component';
import { ProgressbarComponent } from '../../../core/labels/progressbar.component';
import { EmptyStateComponent } from '../../../core/panels/empty-state.component';
import { ActionRowComponent } from '../../../core/panels/action-row.component';
import { ColumnComponent } from '../../../core/table/column.component';
import { TableComponent } from '../../../core/table/table.component';

interface SirOpenRequest {
  source: 'viewSingleItem' | 'viewSelectedItems';
  dcns: string[];
  dcn?: string | null;
  event?: Event;
}

@Component({
  selector: 'app-documents',
  imports: [
    TableComponent,
    ColumnComponent,
    RouterModule,
    ReactiveFormsModule,
    ButtonComponent,
    PageHeaderComponent,
    ActionRowComponent,
    EmptyStateComponent,
    ProgressbarComponent,
    LinkLabelComponent,
  ],
  template: `
    <app-page-header label="Documents"></app-page-header>
    @if (loading) {
      <app-empty-state
        height="300px"
        icon="fa-light fa-file-arrow-down"
        label="Loading documents"
        message="Retrieving from document repository"
      >
        <div class="tw-py-4">
          <app-progressbar />
        </div>
      </app-empty-state>
    } @else {
      <div class="tw-w-full tw-mt-4">
        <app-action-row useMobileLayout>
          @if (!isLeaveLink) {
            <app-button
              (click)="viewSelectedItems()"
              left1
              [disabled]="imagesTable.getSelection()?.length === 0"
              label="View selected"
            />
          }
          @if (uploadBtnVisible) {
            <app-button
              emphasis="Low"
              routerLink="../upload"
              left2
              label="Upload"
            />
          }
        </app-action-row>
      </div>
      <app-table
        #imagesTable
        [dataSource]="dataSource"
        [pageSize]="10"
        [showExport]="false"
        [scrollable]="true"
        scrollHeight="1000px"
        [showFilterButton]="false"
        [globalFilterFields]="['Text']"
        [showFilter]="false"
        [showSelection]="!isLeaveLink"
        (selectedEvent)="onSelection($event)"
        (selectAllEvent)="onSelectAll($event)"
        [recordLabel]="'document'"
        [loading]="loading"
      >
        <app-column
          name="dcn"
          label="Document control#"
          width="20%"
          filterType="text"
        >
          <ng-template let-data>
            @if (isLeaveLink) {
              {{ data.dcn }}
            } @else {
              <app-link-label (click)="viewSingleItem(data, $event)">{{
                data.dcn
              }}</app-link-label>
            }
          </ng-template>
        </app-column>
        <app-column
          name="dateReceived"
          format="d"
          label="Receipt date"
          width="15%"
          filterType="date"
        />
        <app-column
          name="documentType"
          label="Document type"
          width="15%"
          filterType="select"
        />

        <app-column name="note" label="SIR note" width="40%" filterType="text">
          <ng-template let-data>
            <div class="tw-full" style="text-wrap:auto">{{ data.note }}</div>
          </ng-template>
        </app-column>

        @if (unreadImagesVisible) {
          <app-column
            name="status"
            label="Status"
            width="10%"
            filterType="text"
          >
            <ng-template let-data>
              <div class="tw-full" style="text-wrap:auto">
                {{ data.status }}
              </div>
            </ng-template>
          </app-column>
        }
      </app-table>
    }
  `,
  styles: `
    .imageFormPadding {
      padding-top: 2px;
    }
  `,
})
export class DocumentsComponent extends BaseComponent implements OnInit {
  private snackbar = inject(Snackbar);
  private destroyRef = inject(DestroyRef);

  private readonly sirOpenRequest$ = new Subject<SirOpenRequest>();

  claim: ClaimRow;
  loading = true;
  visible = false;
  uploadBtnVisible = false;
  unreadImagesVisible = false;
  isLeaveLink = false;
  image: DocImage[] = [];
  disableViewSelected = true;
  dataSource: any[] = DocumentMock.instance.mock;
  dcnsList: string[] = [];
  conduentSSO: SirSSO;
  item: any;
  imageSrc = '';

  @ViewChild(TableComponent)
  childTable: TableComponent;

  ngOnInit(): void {
    this.sirOpenRequest$
      .pipe(
        exhaustMap((request) =>
          from(this.executeSirOpen(request)).pipe(
            catchError((): typeof EMPTY => EMPTY),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    void this.loadDocuments();
  }

  private async loadDocuments(): Promise<void> {
    this.view.hideLoading();
    this.uploadBtnVisible = this.view.authorize('UploadImages');
    this.unreadImagesVisible = this.view.authorize('UnreadImages');
    this.claim = this.view.currentClaim();
    this.isLeaveLink = this.claim.recordSource === 'L';
    try {
      const images: DocImage[] = await this.api.document.getImage(
        this.claim.recordSource,
        this.claim.recordId,
      );

      this.dataSource = images.map((img) => ({
        dcn: img.dcn,
        documentType: img.documentType,
        note: img.note,
        status: img.status,
        dateReceived: img.dateReceived ? new Date(img.dateReceived) : null,
      }));
    } catch (e: unknown) {
      this.dataSource = [];
      const message =
        e instanceof HttpErrorResponse
          ? e.statusText || e.message
          : 'Unable to load documents';
      this.snackbar.show(message, 'Error');
    } finally {
      this.loading = false;
    }
  }

  onSelection(selections: any[]) {
    this.hasSelections(selections);
  }

  hasSelections(selections: any[]) {
    if (selections && selections?.length > 0) {
      this.disableViewSelected = false;
    } else {
      this.disableViewSelected = true;
    }
  }

  onSelectAll(event: any) {
    this.disableViewSelected = event.checked;
  }

  viewSingleItem(item: any, event?: Event): void {
    if (this.isLeaveLink || item?.dcn == null) {
      return;
    }

    this.sirOpenRequest$.next({
      source: 'viewSingleItem',
      dcns: [item.dcn],
      dcn: item.dcn,
      event,
    });
  }

  viewSelectedItems(): void {
    if (this.isLeaveLink) {
      return;
    }

    const selectedRows = this.childTable?.getSelection() || [];
    const dcnNumbers = selectedRows.map((row) => row.dcn);
    if (dcnNumbers.length === 0) {
      return;
    }

    this.sirOpenRequest$.next({
      source: 'viewSelectedItems',
      dcns: dcnNumbers,
    });
  }

  private async executeSirOpen(request: SirOpenRequest): Promise<void> {
    this.visible = false;
    const dcnList: SirSSO = { dcn: request.dcns } as SirSSO;

    try {
      this.conduentSSO = await this.api.document.viewImage(
        this.claim.recordSource,
        this.claim.recordId,
        dcnList,
      );

      if (this.conduentSSO?.samlResponse) {
        samlPost(
          this.conduentSSO.samlResponse,
          this.conduentSSO.url,
          'samlResponse',
          undefined,
          '_blank',
        );
      } else {
        this.snackbar.show('Single sign-on failed', 'Error');
      }
    } catch (e: unknown) {
      const message =
        e instanceof HttpErrorResponse
          ? e.statusText || e.message
          : 'Single sign-on failed';
      this.snackbar.show(message, 'Error');
      throw e;
    }
  }

  openSsoForm(samlResponse: string, url: string) {
    samlPost(samlResponse, url, 'SAMLResponse');
  }
}
